import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import config from '../config';

function Settings() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newRule, setNewRule] = useState({
    name: ''
  });
  const [user, setUser] = useState(null);

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem('user'));
    setUser(currentUser);
    
    if (currentUser && currentUser.role === 'admin') {
      loadRules();
    } else {
      window.location.href = '/login';
    }
  }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${config.apiBaseUrl}/api/rule-definitions`);
      setRules(response.data || []);
    } catch (error) {
      console.error('Error loading rules:', error);
      setError('Failed to load rule definitions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRule = async () => {
    if (!newRule.name.trim()) {
      setError('Rule name is required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      const response = await axios.post(`${config.apiBaseUrl}/api/rule-definitions`, {
        name: newRule.name.trim()
      });

      setSuccess('Rule added successfully');
      setNewRule({ name: '' });
      await loadRules();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error adding rule:', error);
      setError(error.response?.data?.message || 'Failed to add rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Are you sure you want to delete this rule? This will also remove it from all agent configurations.')) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      await axios.delete(`${config.apiBaseUrl}/api/rule-definitions/${id}`);
      setSuccess('Rule deleted successfully');
      await loadRules();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting rule:', error);
      setError(error.response?.data?.message || 'Failed to delete rule');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveUp = async (ruleId) => {
    const ruleIndex = rules.findIndex(r => r._id === ruleId);
    
    if (ruleIndex === 0) return;
    
    // Swap priorities
    const currentRule = rules[ruleIndex];
    const previousRule = rules[ruleIndex - 1];
    
    const priorityUpdates = [
      { id: currentRule._id, priority: previousRule.priority },
      { id: previousRule._id, priority: currentRule.priority }
    ];

    try {
      setSaving(true);
      await axios.put(`${config.apiBaseUrl}/api/rule-definitions/priorities`, {
        rules: priorityUpdates
      });
      setSuccess('Priority updated');
      await loadRules();
      setTimeout(() => setSuccess(''), 2000);
    } catch (error) {
      console.error('Error updating priority:', error);
      setError('Failed to update priority');
      await loadRules();
    } finally {
      setSaving(false);
    }
  };

  const handleMoveDown = async (ruleId) => {
    const ruleIndex = rules.findIndex(r => r._id === ruleId);
    
    if (ruleIndex === rules.length - 1) return;
    
    // Swap priorities
    const currentRule = rules[ruleIndex];
    const nextRule = rules[ruleIndex + 1];
    
    const priorityUpdates = [
      { id: currentRule._id, priority: nextRule.priority },
      { id: nextRule._id, priority: currentRule.priority }
    ];

    try {
      setSaving(true);
      await axios.put(`${config.apiBaseUrl}/api/rule-definitions/priorities`, {
        rules: priorityUpdates
      });
      setSuccess('Priority updated');
      await loadRules();
      setTimeout(() => setSuccess(''), 2000);
    } catch (error) {
      console.error('Error updating priority:', error);
      setError('Failed to update priority');
      await loadRules();
    } finally {
      setSaving(false);
    }
  };

  // Only show for admin
  if (user && user.role !== 'admin') {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Settings - Rule Definitions
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Define rules that act as categories. Rules at the top have higher priority. These rules will be available as categories when configuring agents.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Add New Rule */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
          <Typography variant="h6" gutterBottom>
            Add New Rule (Category)
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Rule Name"
                value={newRule.name}
                onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                placeholder="Enter rule name (e.g., 'Request Completed', 'Emergency Appointment')"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddRule();
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleAddRule}
                disabled={saving || !newRule.name.trim()}
                startIcon={<AddIcon />}
                sx={{ height: '56px' }}
              >
                Add Rule
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {/* Rules List */}
            {rules.length > 0 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Rules ({rules.length} total)
                </Typography>
                <List>
                  {rules.sort((a, b) => a.priority - b.priority).map((rule, index) => (
                    <ListItem
                      key={rule._id}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: 'white'
                      }}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            edge="end"
                            onClick={() => handleMoveUp(rule._id)}
                            disabled={index === 0 || saving}
                            size="small"
                            title="Move up (higher priority)"
                          >
                            ↑
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleMoveDown(rule._id)}
                            disabled={index === rules.length - 1 || saving}
                            size="small"
                            title="Move down (lower priority)"
                          >
                            ↓
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleDeleteRule(rule._id)}
                            disabled={saving}
                            color="error"
                            title="Delete rule"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={rule.name}
                        secondary={`Priority: ${index + 1} (${index === 0 ? 'Highest' : index === rules.length - 1 ? 'Lowest' : 'Medium'})`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {rules.length === 0 && (
              <Alert severity="info">
                No rules defined yet. Add a rule above to get started. Rules you create will act as categories when configuring agents.
              </Alert>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default Settings;
